import * as crypto from 'crypto'
import { XMLParser, XMLBuilder, XmlBuilderOptions } from 'fast-xml-parser'
import { SAMLAuthRequest, SAMLAssertion, ParsedSAMLResponse, SAMLUserProfile, SAMLConfig } from './types'
import { ValidationError, AuthenticationError } from '../errors'

/**
 * SAML Utility Functions
 */

/**
 * Generate SAML Auth Request XML
 */
export function generateSAMLAuthRequest(config: SAMLConfig): { xml: string; id: string; issueInstant: string } {
  const id = `_${generateRandomId()}`
  const issueInstant = new Date().toISOString()

  const authRequest: SAMLAuthRequest = {
    id,
    issueInstant,
    destination: config.idp.ssoUrl,
    assertionConsumerServiceURL: config.sp.acsUrl,
    issuer: config.sp.entityID,
    nameIDFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress'
  }

  const xml = buildAuthRequest(authRequest)

  return { xml, id, issueInstant }
}

/**
 * Build AuthnRequest XML
 */
export function buildAuthRequest(request: SAMLAuthRequest): string {
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    processEntities: false
  })

  const authRequest = {
    'samlp:AuthnRequest': {
      '@_xmlns:samlp': 'urn:oasis:names:tc:SAML:2.0:protocol',
      '@_xmlns:saml': 'urn:oasis:names:tc:SAML:2.0:assertion',
      '@_ID': request.id,
      '@_Version': '2.0',
      '@_IssueInstant': request.issueInstant,
      '@_Destination': request.destination,
      '@_AssertionConsumerServiceURL': request.assertionConsumerServiceURL,
      '@_ProtocolBinding': 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST',
      'saml:Issuer': {
        '@_Format': 'urn:oasis:names:tc:SAML:2.0:nameid-format:entity',
        '#text': request.issuer
      },
      'samlp:NameIDPolicy': {
        '@_Format': request.nameIDFormat,
        '@_AllowCreate': 'true'
      }
    }
  }

  return builder.build(authRequest) as string
}

/**
 * Decode Base64 SAML Response
 */
export function decodeSAMLResponse(encodedResponse: string): string {
  return Buffer.from(encodedResponse, 'base64').toString('utf-8')
}

/**
 * Encode SAML Request to Base64
 */
export function encodeSAMLRequest(xml: string): string {
  return Buffer.from(xml).toString('base64')
}

/**
 * Parse SAML Response XML
 */
export function parseSAMLResponse(samlResponseXML: string, config: SAMLConfig): ParsedSAMLResponse {
  try {
    const parser = new XMLParser({
      ignoreAttributes: false,
      parseAttributeValue: true,
      parseTagValue: true,
      allowBooleanAttributes: true
    })

    const parsed = parser.parse(samlResponseXML) as any

    // Extract response and assertion
    const response = extractResponse(parsed)
    if (!response) {
      return {
        valid: false,
        error: 'Invalid SAML response structure'
      }
    }

    // Validate signature (if required)
    if (config.wantResponseSigned || config.wantAssertionsSigned) {
      const isValid = validateSignature(samlResponseXML, config.idp.certificate)
      if (!isValid) {
        return {
          valid: false,
          error: 'SAML response signature validation failed'
        }
      }
    }

    // Validate timing
    const timingValid = validateTiming(response)
    if (!timingValid) {
      return {
        valid: false,
        error: 'SAML response expired or not yet valid'
      }
    }

    // Validate recipient
    if (response.assertion.recipient !== config.sp.acsUrl) {
      return {
        valid: false,
        error: 'SAML response recipient does not match ACS URL'
      }
    }

    return {
      valid: true,
      assertion: response.assertion,
      relayState: response.relayState
    }
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Failed to parse SAML response'
    }
  }
}

/**
 * Extract SAML Assertion from Response
 */
function extractResponse(parsed: any): any {
  const samlResponse = parsed['samlp:Response'] || parsed['Response']

  if (!samlResponse) {
    return null
  }

  const assertion = samlResponse['saml:Assertion'] || samlResponse['Assertion']

  if (!assertion) {
    return null
  }

  const nameID = assertion['saml:Subject']?.['saml:NameID'] || assertion['Subject']?.['NameID']
  const attributes = parseAttributes(assertion)

  return {
    assertion: {
      id: assertion['@_ID'],
      issueInstant: assertion['@_IssueInstant'],
      notOnOrAfter: assertion['saml:Conditions']?.['@_NotOnOrAfter'] || assertion['Conditions']?.['@_NotOnOrAfter'],
      recipient: assertion['saml:SubjectConfirmationData']?.['@_Recipient'] || assertion['SubjectConfirmationData']?.['@_Recipient'],
      inResponseTo: samlResponse['@_InResponseTo'],
      issuer: assertion['saml:Issuer'] || assertion['Issuer'],
      sessionIndex: assertion['saml:AuthnStatement']?.['@_SessionIndex'],
      nameID: {
        value: nameID?.['#text'] || nameID,
        format: nameID?.['@_Format']
      },
      attributes
    },
    relayState: samlResponse['@_RelayState']
  }
}

/**
 * Parse SAML Attributes
 */
function parseAttributes(assertion: any): Record<string, string | string[]> {
  const attributes: Record<string, string | string[]> = {}

  const attributeStatement = assertion['saml:AttributeStatement'] || assertion['AttributeStatement']
  if (!attributeStatement) {
    return attributes
  }

  const attributeList = Array.isArray(attributeStatement['saml:Attribute'])
    ? attributeStatement['saml:Attribute']
    : attributeStatement['saml:Attribute']
      ? [attributeStatement['saml:Attribute']]
      : []

  for (const attr of attributeList) {
    const name = attr['@_Name']
    const values = Array.isArray(attr['saml:AttributeValue'])
      ? attr['saml:AttributeValue'].map((v: any) => v['#text'] || v)
      : [attr['saml:AttributeValue']?.['#text'] || attr['saml:AttributeValue']]

    attributes[name] = values.length === 1 ? values[0] : values
  }

  return attributes
}

/**
 * Validate SAML Response Signature
 */
export function validateSignature(samlXML: string, certificate: string): boolean {
  try {
    // Extract signature from XML
    const signatureMatch = samlXML.match(/<Signature.*?<\/Signature>/s)
    if (!signatureMatch) {
      return false
    }

    const signatureElement = signatureMatch[0]

    // Extract signed info and signature value
    const signedInfoMatch = samlXML.match(/<SignedInfo.*?<\/SignedInfo>/s)
    const signatureValueMatch = signatureElement.match(/<SignatureValue>(.*?)<\/SignatureValue>/s)

    if (!signedInfoMatch || !signatureValueMatch) {
      return false
    }

    const signedInfo = signedInfoMatch[0]
    const signatureValue = signatureValueMatch[1]

    // Verify signature
    const verifier = crypto.createVerify('RSA-SHA256')
    verifier.update(signedInfo)

    const certificatePublicKey = `-----BEGIN CERTIFICATE-----\n${certificate}\n-----END CERTIFICATE-----`

    return verifier.verify(certificatePublicKey, Buffer.from(signatureValue, 'base64'))
  } catch (error) {
    console.error('Signature validation error:', error)
    return false
  }
}

/**
 * Validate SAML Response Timing
 */
function validateTiming(response: any): boolean {
  const now = new Date()
  const issueInstant = new Date(response.assertion.issueInstant)
  const notOnOrAfter = new Date(response.assertion.notOnOrAfter)

  // Allow 5 minutes clock skew
  const clockSkew = 5 * 60 * 1000

  return now.getTime() - issueInstant.getTime() < clockSkew && now.getTime() < notOnOrAfter.getTime() + clockSkew
}

/**
 * Extract SAML User Profile
 */
export function extractUserProfile(assertion: SAMLAssertion, attributeMap: Record<string, string> = {}): SAMLUserProfile {
  const defaultMap: Record<string, string> = {
    email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    firstName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
    lastName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
    name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
    phoneNumber: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/mobilephone',
    groups: 'http://schemas.xmlsoap.org/claims/Group',
    uid: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
  }

  const finalMap = { ...defaultMap, ...attributeMap }

  const profile: SAMLUserProfile = {
    uid: assertion.nameID.value,
    attributes: assertion.attributes
  }

  // Map SAML attributes to user profile
  for (const [key, attrName] of Object.entries(finalMap)) {
    const value = assertion.attributes[attrName]
    if (value) {
      if (key === 'groups') {
        profile.groups = Array.isArray(value) ? value : [value]
      } else {
        ;(profile as any)[key] = Array.isArray(value) ? value[0] : value
      }
    }
  }

  return profile
}

/**
 * Generate SP Metadata XML
 */
export function generateSPMetadata(config: SAMLConfig): string {
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true
  })

  const metadata = {
    'md:EntityDescriptor': {
      '@_xmlns:md': 'urn:oasis:names:tc:SAML:2.0:metadata',
      '@_xmlns:ds': 'http://www.w3.org/2000/09/xmldsig#',
      '@_entityID': config.sp.entityID,
      'md:SPSSODescriptor': {
        '@_AuthnRequestsSigned': 'true',
        '@_WantAssertionsSigned': config.wantAssertionsSigned ? 'true' : 'false',
        '@_protocolSupportEnumeration': 'urn:oasis:names:tc:SAML:2.0:protocol',
        'md:KeyDescriptor': {
          '@_use': 'signing',
          'ds:KeyInfo': {
            'ds:X509Data': {
              'ds:X509Certificate': config.sp.certificate
            }
          }
        },
        'md:NameIDFormat': 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
        'md:AssertionConsumerService': {
          '@_Binding': 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST',
          '@_Location': config.sp.acsUrl,
          '@_index': '0',
          '@_isDefault': 'true'
        },
        ...(config.sp.sloUrl && {
          'md:SingleLogoutService': {
            '@_Binding': 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect',
            '@_Location': config.sp.sloUrl
          }
        })
      }
    }
  }

  return builder.build(metadata) as string
}

/**
 * Generate Random ID for SAML requests
 */
function generateRandomId(): string {
  return crypto.randomBytes(16).toString('hex')
}

/**
 * Create SAML Request State
 */
export function createSAMLRequestState(requestId: string, relayState?: string) {
  return {
    id: `_${generateRandomId()}`,
    requestId,
    relayState,
    createdAt: new Date(),
    expiresAt: new Date(Date.now() + 10 * 60 * 1000) // 10 minutes
  }
}

/**
 * Validate SAML Request State
 */
export function validateSAMLRequestState(state: any, requestId: string): boolean {
  if (!state) {
    return false
  }

  if (new Date(state.expiresAt) < new Date()) {
    return false
  }

  return state.requestId === requestId
}
